import React from "react";
import { CopilotAutoReschedulingNotificationsCallout } from "owa-auto-rescheduling-notification-callout";
import classNames from "owa-classnames";
import type { OwaHeaderProps } from "owa-header-types";
import { getBposNavBarData } from "owa-bpos-store";
import { isCapabilityEnabled, isCapabilitySupported } from "owa-capabilities";
import { customNotificationSoundCapability } from "owa-capabilities-definitions/lib/customNotificationSoundCapability";
import { novaMeControlCapability } from "owa-capabilities-definitions/lib/novaMeControlCapability";
import type { MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { OWA_HEADER_BUTTON_GROUP_LEFT_REGION_ID, OWA_HEADER_BUTTON_GROUP_RIGHT_REGION_ID, OWA_TITLE_BAR_ID, } from "owa-header-constants";
import { MeControlLauncher } from "owa-mecontrol-launcher";
import { LazyNotificationSoundCustomizationTeachingCallout } from "owa-notification-sound-customization-teaching-callout";
import { MergedNotificationPane } from "owa-header-charms";
import { PendingAccountsWelcomeCallout as LazyPendingAccountsWelcomeCallout, ICloudSignInTeachingCallout as LazyICloudSignInTeachingCallout, } from "owa-mail-accounts-view-callout";
import { observer } from "owa-mobx-react";
import { LazyOwaMonarchReferralCampaign } from "owa-new-outlook-promo-dialog";
import { LazyPstTeachingCallout } from "owa-pst-teaching-callout";
import { LazyF6TeachingBubble } from "owa-accessibility-loop-teaching-bubble";
import { RepromptDialog as LazyRepromptDialog, RepromptDialogV2 as LazyRepromptDialogV2, } from "owa-reprompt-dialog";
import type NavBarData from "owa-service/lib/contract/NavBarData";
import { LazyAddSharedMailboxesAsAccountTeachingCallout } from "owa-shared-with-me-teaching-callout";
import { HEADER_RESPONSIVE_BREAKPOINT } from "../constants/constants";
import useHeaderResponsive from "../hooks/useHeaderResponsive";
import getLeftCharmsContainerStyle from "../utils/charms/getLeftCharmsContainerStyle";
import OwaHeaderButtonGroup from "./buttons/OwaHeaderButtonGroup";
import OwaHeaderAppIcon from "./charms/OwaHeaderAppIcon";
import OwaHeaderAppLauncher from "./charms/OwaHeaderAppLauncher";
import OwaHeaderBrandingLabel from "./charms/OwaHeaderBrandingLabel";
import OwaHeaderDevSourceLabel from "./charms/OwaHeaderDevSourceLabel";
import OwaHeaderTenantLogo from "./charms/OwaHeaderTenantLogo";
import OwaHeaderMeControl from "./mecontrol/OwaHeaderMeControl";
import OwaShyHeaderWrapper from "./shyHeader/OwaShyHeaderWrapper";
import { headerRoot, headerLeft, headerCenter, headerCharmsAndSearch, shrinkableHeaderCharmsAndSearch, headerLeftCharms, headerSearchScopePicker, headerSearchBox, centeredSearchBox, } from "./OwaHeader.scss";

const defaultExport: React.FunctionComponent<OwaHeaderProps> = observer(function OwaHeader(props: OwaHeaderProps) {
    const navBarData = getBposNavBarData() as NavBarData | null;
    const waffleButtonRef = React.useRef<HTMLButtonElement>(null);
    const isOverlayEnabled = isWin32StylingEnabled();
    
    const leftHeaderButtons = React.useMemo(() => isOverlayEnabled
        ? props.headerButtons.filter(button => button.overlayPlacement === "left")
        : [], [isOverlayEnabled, props.headerButtons]);
    
    const rightHeaderButtons = React.useMemo(() => isOverlayEnabled
        ? props.headerButtons.filter(button => button.overlayPlacement !== "left")
        : props.headerButtons, [isOverlayEnabled, props.headerButtons]);
    
    const charmsAndSearchRef = useHeaderResponsive(HEADER_RESPONSIVE_BREAKPOINT, {
        searchBoxRespond: props.searchBoxRespond,
        currentModule: props.currentModule,
    });
    
    const header = (<div id={OWA_TITLE_BAR_ID} className={headerRoot}>
            <OwaShyHeaderWrapper waffleButtonRef={waffleButtonRef}>
                <div className={headerLeft}>
                    <OwaHeaderAppIcon />
                    {!props.disableWaffle && (<OwaHeaderAppLauncher buttonRef={waffleButtonRef} navBarData={navBarData}/>)}
                    {leftHeaderButtons.length > 0 && (<OwaHeaderButtonGroup buttons={leftHeaderButtons} regionId={OWA_HEADER_BUTTON_GROUP_LEFT_REGION_ID}/>)}
                </div>
                <div className={headerCenter}>
                    <div className={classNames(headerCharmsAndSearch, props.centerSearchBox && shrinkableHeaderCharmsAndSearch)} ref={
        // TODO (456964): Remove this React 18 compatibility cast after adopting React 19 types.
        charmsAndSearchRef as React.Ref<HTMLDivElement>}>
                        <div className={headerLeftCharms} style={getLeftCharmsContainerStyle(props.searchAlignmentWidth)}>
                            <OwaHeaderDevSourceLabel />
                            <OwaHeaderTenantLogo navBarData={navBarData}/>
                            {!isOverlayEnabled && (<OwaHeaderBrandingLabel label={props.brandingLabel}/>)}
                            <div className={headerSearchScopePicker}>
                                {props.renderSearchScopePicker?.()}
                            </div>
                        </div>
                        <div className={classNames(headerSearchBox, props.centerSearchBox && centeredSearchBox)}>
                            {props.renderSearch?.()}
                        </div>
                        {isOverlayEnabled && <OwaHeaderMeControl />}
                        {rightHeaderButtons.length > 0 && (<OwaHeaderButtonGroup buttons={rightHeaderButtons} regionId={OWA_HEADER_BUTTON_GROUP_RIGHT_REGION_ID}/>)}
                    </div>
                </div>
                {!isOverlayEnabled && <OwaHeaderMeControl />}
            </OwaShyHeaderWrapper>
        </div>);
    
    return (<>
            <LazyAddSharedMailboxesAsAccountTeachingCallout />
            {isCapabilitySupported(customNotificationSoundCapability) && (<LazyNotificationSoundCustomizationTeachingCallout />)}
            {isGlobalFeatureEnabled("acct-owaMonarchReferral") && (<LazyOwaMonarchReferralCampaign />)}
            {isGlobalFeatureEnabled("acct-pstFileSupport") && <LazyPstTeachingCallout />}{" "}
            <LazyF6TeachingBubble />
            {isGlobalFeatureEnabled("settings-repromptDialog-v2") ? (<LazyRepromptDialogV2 />) : (<LazyRepromptDialog />)}
            {isGlobalFeatureEnabled("time-copilot-managed-events") && (<CopilotAutoReschedulingNotificationsCallout />)}
            <LazyPendingAccountsWelcomeCallout />
            <LazyICloudSignInTeachingCallout />
            <MergedNotificationPane key="notificationPane"/>
            {getWrappedHeader(header, props)}
        </>);
}, "OwaHeader");
export default defaultExport;

function getWrappedHeader(header: React.ReactElement, props: OwaHeaderProps): React.ReactNode {
    // Conditionally wrap the header with the MeControl container or custom wrapper
    if (props.wrapSuiteHeader) {
        const novaMeControl = isCapabilityEnabled(novaMeControlCapability)
            ? (MeControlLauncher as React.FC<{
                mailboxInfo: MailboxInfo;
            }>)
            : undefined;
        
        return props.wrapSuiteHeader(header, novaMeControl);
    }
    else {
        return header;
    }
}
